import { useMutation, useQuery } from '@apollo/client'
import { Button } from '@material-ui/core'
import AddBoxIcon from '@material-ui/icons/AddBox'
import AssignmentIcon from '@material-ui/icons/Assignment'
import React, { useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import apolloClient from '../../graphql/apolloClient'
import { WALLET_QUERY } from '../../graphql/wallet'
import { ADD_CREDITOR_MUTATION } from '../../graphql/walletCreditors'
import { DateObject } from '../../helpers/date'
import { isValidCreditLimit } from '../../helpers/numbers'
import { getTranslate, lang } from '../../localization'
import { Wallet } from '../../types/wallet'
import {
  AddCreditorsRequestInput,
  CreditorInput
} from '../../types/walletCreditor'
import StepContent from './stepContents'
import useStyles from './styles/dynamicStepper.styles'

interface Step {
  label: 'Create Creditors' | 'Payment Term'
  icon: React.ReactNode
}

/**
 * Whether the stepper's Next/Finish button is disabled, as a pure function so it can be
 * tested without mounting the stepper and its whole dependency tree.
 *
 * Extracting it also removed a trap: the step-0 branch read the COMPONENT's `activeStep`
 * from the closure while the step-1 branch read the `active_step` PARAMETER. Inert only
 * because the single caller passed the same value to both; a second caller, or a reorder,
 * would have made the two branches disagree about which step they were judging.
 */
export const isNextDisabled = (
  activeStep: number,
  creditors: CreditorInput[],
  assignmentHasFinished: boolean
): boolean => {
  if (activeStep === 0) {
    // Step 2: "Create Creditor" (only for bilateral wallets)
    //
    // Limits use the SHARED rule rather than `<= 0`. Requiring strictly positive values
    // made the commonest creditor impossible to create through this screen: 38 of the 47
    // creditors on stage carry zero for both limits, and the wallet service stores zero
    // happily -- `ValidateCreditLimit` rejects only negative, NaN and infinite values.
    //
    // An operator creating a zero-limit creditor got a Next/Finish button that stayed dead
    // with nothing on screen saying why. Same defect as the wallet Save button and the
    // creditor edit modal before it; this was the third copy of it.
    return (
      creditors.length === 0 ||
      creditors.some(
        (creditor) =>
          !creditor.entity.entityType ||
          !creditor.entity.entityID ||
          !isValidCreditLimit(creditor.maxCredit) ||
          !isValidCreditLimit(creditor.maxReservable)
      )
    )
  }

  if (activeStep === 1) {
    // Step 3: Payment Term
    return !assignmentHasFinished
  }

  return false
}

const CreditorDynamicStepper: React.FC = () => {
  const classes = useStyles()
  const history = useHistory()
  const params = useParams<{ walletId: string }>()
  const { data: wallet } = useQuery<{
    wallet: Wallet
  }>(WALLET_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    variables: {
      walletID: params.walletId
    }
  })
  const [activeStep, setActiveStep] = useState<number>(0)
  const [steps] = useState<Step[]>([
    {
      label: 'Create Creditors',
      icon: <AddBoxIcon style={{ fontSize: 36 }} />
    },
    {
      label: 'Payment Term',
      icon: <AssignmentIcon style={{ fontSize: 36 }} />
    }
  ])
  const [coveringRange, setcoveringRange] = useState<DateObject>({})
  const [hasFinishedAssigning, sethasFinishedAssigning] =
    useState<boolean>(false)
  // Initialize state for wallet input data
  const [creditorsInput, setcreditorsInput] =
    useState<AddCreditorsRequestInput>({
      walletID: params.walletId,
      creditors: []
    })
  const [addCreditors, { loading: loadingForCreditorCreation }] = useMutation(
    ADD_CREDITOR_MUTATION,
    {
      onCompleted: (data) => {
        toast.success(
          data.addCreditors.message || 'creditors created successfully!'
        )
      },
      onError: (error) => {
        toast.error(`Error creating creditors: ${error.message}`)
      }
    }
  )

  const handleNext = (): void => {
    if (activeStep === 1) {
      addCreditors({ variables: { input: creditorsInput } })
      history.push(`/${lang}/adopay/wallet/${params.walletId}/details`)
      apolloClient.cache.evict({
        fieldName: 'wallet'
      })
    } else {
      setActiveStep((prevActiveStep) => prevActiveStep + 1)
    }
  }

  const handleBack = (): void => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1)
  }

  return (
    <div className={classes.root}>
      <h2>{getTranslate('Create Wallet')}</h2>
      <div className={classes.stepperContainer}>
        <div className={classes.customStepper}>
          {steps.map((step, index) => (
            <React.Fragment key={index}>
              <div className={classes.stepIconContainer}>
                <div
                  className={
                    activeStep === index
                      ? `${classes.stepCircle} ${classes.activeStepCircle}`
                      : activeStep > index
                      ? `${classes.stepCircle} ${classes.pastStepCircle}`
                      : `${classes.stepCircle} ${classes.inactiveStepCircle}`
                  }>
                  {step.icon}
                </div>
                <div
                  className={
                    activeStep === index
                      ? `${classes.stepLabel} ${classes.activeStepLabel}`
                      : activeStep > index
                      ? `${classes.stepLabel} ${classes.pastStepLabel}`
                      : `${classes.stepLabel} ${classes.inactiveStepLabel}`
                  }>
                  {getTranslate(step.label)}
                </div>
              </div>
              {(index < steps.length - 1 || steps.length === 1) && (
                <div
                  className={[
                    activeStep > index
                      ? classes.activeConnectorLine
                      : classes.inactiveConnectorLine,
                    steps.length === 1 && classes.singleConnectorLine
                  ].join(' ')}></div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
      <div className={classes.stepContent}>
        <StepContent
          step={activeStep}
          creditorsInput={creditorsInput}
          setcreditorsInput={setcreditorsInput}
          coveringRange={coveringRange}
          setcoveringRange={setcoveringRange}
          sethasFinishedAssigning={sethasFinishedAssigning}
          currency={wallet?.wallet.currency || 'IRR'}
        />
      </div>
      <div className={classes.buttonContainer}>
        <Button
          className={classes.button}
          disabled={activeStep === 0}
          onClick={handleBack}
          variant="outlined"
          color="primary">
          {getTranslate('Back')}
        </Button>
        <Button
          className={classes.button}
          onClick={handleNext}
          disabled={
            isNextDisabled(
              activeStep,
              creditorsInput.creditors,
              hasFinishedAssigning
            ) || loadingForCreditorCreation
          }
          variant="contained"
          color="primary">
          {activeStep === 1 && steps.length !== 1
            ? getTranslate('Finish')
            : getTranslate('Next')}
        </Button>
      </div>
    </div>
  )
}

export default CreditorDynamicStepper
